/******/ (() => { // webpackBootstrap
class ArticleStickyNav extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
    this.handleViewportChange = this.handleViewportChange.bind(this);
    this.intersectionObserver = null;
    this.headingElements = [];
    this.linkElements = [];
  }

  connectedCallback() {
    window.wetheme.webcomponentRegistry.register({ key: 'component-article-sticky-nav' });
    this.navElement = this.querySelector('.template-article__sticky-nav-list');
    this.sectionElement = this.closest('[data-wetheme-section-type="blog-post"]');
    this.articleBodyElement = this.sectionElement?.querySelector('.template-article__article [itemprop="articleBody"]');

    this.addEventListener('click', this.handleClick);
    window.addEventListener('resize', this.handleViewportChange);
    document.addEventListener('headerSticked', this.handleViewportChange);
    document.addEventListener('headerUnstuck', this.handleViewportChange);

    this.init();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    window.removeEventListener('resize', this.handleViewportChange);
    document.removeEventListener('headerSticked', this.handleViewportChange);
    document.removeEventListener('headerUnstuck', this.handleViewportChange);
    this.disconnectObserver();
  }

  init() {
    if (!this.navElement || !this.articleBodyElement) {
      this.hidden = true;
      return;
    }

    const headings = Array.from(this.articleBodyElement.querySelectorAll('h2')).filter(heading => heading.textContent.trim().length > 0);

    if (!headings.length) {
      this.hidden = true;
      return;
    }

    const usedIds = new Set(Array.from(this.sectionElement.querySelectorAll('[id]')).map(element => element.id).filter(Boolean));
    const fragment = document.createDocumentFragment();

    this.headingElements = headings.map(heading => {
      const headingId = this.ensureHeadingId(heading, usedIds);
      const link = document.createElement('a');

      link.href = `#${headingId}`;
      link.className = 'template-article__sticky-link';
      link.dataset.targetId = headingId;
      link.textContent = heading.textContent.trim();

      fragment.appendChild(link);

      return heading;
    });

    this.navElement.replaceChildren(fragment);
    this.linkElements = Array.from(this.navElement.querySelectorAll('[data-target-id]'));
    this.hidden = false;

    this.updateHeadingOffsets();
    this.observeHeadings();
  }

  ensureHeadingId(heading, usedIds) {
    const baseId = this.slugify(heading.id || heading.textContent);
    let uniqueId = baseId;
    let suffix = 2;

    while (usedIds.has(uniqueId)) {
      if (heading.id === uniqueId) {
        break;
      }

      uniqueId = `${baseId}-${suffix}`;
      suffix += 1;
    }

    heading.id = uniqueId;
    usedIds.add(uniqueId);

    return uniqueId;
  }

  slugify(value) {
    const normalizedValue = value
      .toString()
      .trim()
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^\p{Letter}\p{Number}]+/gu, '-')
      .replace(/^-+|-+$/g, '');

    return normalizedValue || 'section';
  }

  handleClick(event) {
    const link = event.target.closest('[data-target-id]');

    if (!link) {
      return;
    }

    const targetId = link.dataset.targetId;
    const targetHeading = this.sectionElement?.querySelector(`#${CSS.escape(targetId)}`);

    if (!targetHeading) {
      return;
    }

    event.preventDefault();

    const top = targetHeading.getBoundingClientRect().top + window.scrollY - this.getScrollOffset();
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    window.scrollTo({
      top,
      behavior: reducedMotion ? 'auto' : 'smooth'
    });

    if (window.history?.replaceState) {
      window.history.replaceState(null, '', `#${targetId}`);
    }

    this.setActiveLink(targetId);
  }

  handleViewportChange() {
    if (!this.headingElements.length) {
      return;
    }

    this.updateHeadingOffsets();
    this.observeHeadings();
  }

  updateHeadingOffsets() {
    const scrollOffset = this.getScrollOffset();

    this.headingElements.forEach(heading => {
      heading.style.scrollMarginTop = `${scrollOffset}px`;
    });
  }

  getScrollOffset() {
    const isDesktop = window.innerWidth >= 768;
    const rootStyles = window.getComputedStyle(document.documentElement);
    const stickyOffset = parseFloat(rootStyles.getPropertyValue(isDesktop ? '--sticky-header-desktop-height' : '--sticky-header-mobile-height')) || 0;
    const headerOffset = parseFloat(rootStyles.getPropertyValue(isDesktop ? '--header-height-desktop' : '--header-height-mobile')) || 0;

    return Math.max(stickyOffset, headerOffset) + 24;
  }

  observeHeadings() {
    this.disconnectObserver();

    if (!('IntersectionObserver' in window) || !this.headingElements.length) {
      this.setActiveLink(this.headingElements[0]?.id);
      return;
    }

    const scrollOffset = this.getScrollOffset();

    this.intersectionObserver = new IntersectionObserver(entries => {
      const visibleEntries = entries
        .filter(entry => entry.isIntersecting)
        .sort((firstEntry, secondEntry) => firstEntry.boundingClientRect.top - secondEntry.boundingClientRect.top);

      if (visibleEntries.length > 0) {
        this.setActiveLink(visibleEntries[0].target.id);
      }
    }, {
      root: null,
      rootMargin: `-${scrollOffset}px 0px -55% 0px`,
      threshold: 0
    });

    this.headingElements.forEach(heading => {
      this.intersectionObserver.observe(heading);
    });

    this.setActiveLink(this.headingElements[0].id);
  }

  disconnectObserver() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
  }

  setActiveLink(activeId) {
    this.linkElements.forEach(link => {
      const isActive = link.dataset.targetId === activeId;
      link.classList.toggle('is-active', isActive);
      link.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }
}

if (!customElements.get('article-sticky-nav')) {
  customElements.define('article-sticky-nav', ArticleStickyNav);
}

/******/ })()
;